"use client"

import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { baseUrl } from "../../config/baseUrl"
import axios from "axios"
import toastify from "../../config/toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faArrowLeft,
    faBoxOpen,
    faPlus,
    faSave,
    faSpinner,
    faTimes,
    faTrash,
    faUpload,
} from "@fortawesome/free-solid-svg-icons"
import JoditEditor from "jodit-react"
import editorConfig from "../../utils/editorConfig"

const VALIDITY_OPTIONS = [
    { value: "3", label: "3 Months", days: 90 },
    { value: "6", label: "6 Months", days: 180 },
    { value: "12", label: "12 Months", days: 365 },
]

const getNormalizedId = (value) => {
    if (!value) return ""
    return typeof value === "string" ? value : value?._id || ""
}

const normalizeValidityValue = (value) => {
    const numericValue = Number(value)

    if (!numericValue) {
        return "3"
    }

    const directMatch = VALIDITY_OPTIONS.find((option) => Number(option.value) === numericValue)
    if (directMatch) {
        return directMatch.value
    }

    const daysMatch = VALIDITY_OPTIONS.find((option) => option.days === numericValue)
    return daysMatch?.value || "3"
}

const getCategoryIdBySubcategory = (subcategoryId, availableSubcategories) => {
    const matchedSubcategory = availableSubcategories.find((subcategory) => subcategory._id === subcategoryId)
    return getNormalizedId(matchedSubcategory?.categoryId)
}

const getDiscountPercent = (price, offerPrice) => {
    const regularPrice = Number(price)
    const discountedPrice = Number(offerPrice)

    if (!regularPrice || !discountedPrice || discountedPrice >= regularPrice) {
        return ""
    }

    return Math.round(((regularPrice - discountedPrice) / regularPrice) * 100).toString()
}

export default function EditTestPackage() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [formval, setFormval] = useState({
        title: "",
        description: "",
        status: "active",
        subcategoryID: "",
        categoryID: "",
    })
    const [pricingPlans, setPricingPlans] = useState([
        { id: 1, validity: "3", price: "", offerPrice: "", discountPercent: "", isPopular: false },
    ])
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [existingImage, setExistingImage] = useState("")
    const [isDragging, setIsDragging] = useState(false)
    const [loading, setLoading] = useState(false)
    const [pageLoading, setPageLoading] = useState(true)
    const [categories, setCategories] = useState([])
    const [subcategories, setSubcategories] = useState([])
    const [packageNotFound, setPackageNotFound] = useState(false)

    const usedValidities = pricingPlans.map((plan) => plan.validity)

    const getNextAvailableValidity = () => {
        return VALIDITY_OPTIONS.find((option) => !usedValidities.includes(option.value))?.value || ""
    }

    const addPricingPlan = () => {
        if (pricingPlans.length >= 3) return

        const nextValidity = getNextAvailableValidity()
        if (!nextValidity) return

        setPricingPlans((previousPlans) => [
            ...previousPlans,
            {
                id: Date.now(),
                validity: nextValidity,
                price: "",
                offerPrice: "",
                discountPercent: "",
                isPopular: false,
            },
        ])
    }

    const handlePopularChange = (idToUpdate, checked) => {
        setPricingPlans((previousPlans) =>
            previousPlans.map((plan) => {
                if (plan.id === idToUpdate) {
                    return { ...plan, isPopular: checked }
                }

                return checked ? { ...plan, isPopular: false } : plan
            }),
        )
    }

    const removePricingPlan = (idToRemove) => {
        if (pricingPlans.length <= 1) {
            return toastify.error("At least one pricing plan is required")
        }

        setPricingPlans((previousPlans) => previousPlans.filter((plan) => plan.id !== idToRemove))
    }

    const updatePlanField = (idToUpdate, field, value) => {
        setPricingPlans((previousPlans) =>
            previousPlans.map((plan) => {
                if (plan.id !== idToUpdate) return plan

                const updatedPlan = { ...plan, [field]: value }

                if (field === "price") {
                    const regularPrice = parseFloat(value) || 0

                    if (regularPrice && updatedPlan.discountPercent !== "") {
                        const discount = parseFloat(updatedPlan.discountPercent) || 0
                        updatedPlan.offerPrice = Math.round(regularPrice * (1 - discount / 100)).toString()
                    } else if (regularPrice && updatedPlan.offerPrice) {
                        const offerPrice = parseFloat(updatedPlan.offerPrice) || 0

                        if (offerPrice <= regularPrice) {
                            updatedPlan.discountPercent = Math.round(
                                ((regularPrice - offerPrice) / regularPrice) * 100,
                            ).toString()
                        }
                    }
                } else if (field === "offerPrice") {
                    const offerPrice = parseFloat(value) || 0
                    const regularPrice = parseFloat(updatedPlan.price) || 0

                    if (regularPrice && offerPrice <= regularPrice) {
                        updatedPlan.discountPercent = Math.round(
                            ((regularPrice - offerPrice) / regularPrice) * 100,
                        ).toString()
                    } else if (regularPrice) {
                        updatedPlan.discountPercent = "0"
                    }
                } else if (field === "discountPercent") {
                    const discount = parseFloat(value) || 0
                    const regularPrice = parseFloat(updatedPlan.price) || 0

                    if (regularPrice && value !== "") {
                        updatedPlan.offerPrice = Math.round(regularPrice * (1 - discount / 100)).toString()
                    }
                }

                return updatedPlan
            }),
        )
    }

    const getValidityLabel = (value) => {
        return VALIDITY_OPTIONS.find((option) => option.value === value)?.label || value
    }

    const changeHandler = (event) => {
        const { name, value } = event.target

        setFormval((previousValue) => ({
            ...previousValue,
            [name]: value,
            ...(name === "categoryID" ? { subcategoryID: "" } : {}),
        }))
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0]
        if (!file) return

        setSelectedFile(file)

        const reader = new FileReader()
        reader.onload = () => {
            setPreviewUrl(reader.result)
        }
        reader.readAsDataURL(file)
    }

    const handleDragOver = (event) => {
        event.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = () => {
        setIsDragging(false)
    }

    const handleDrop = (event) => {
        event.preventDefault()
        setIsDragging(false)

        if (!event.dataTransfer.files || !event.dataTransfer.files[0]) return

        const file = event.dataTransfer.files[0]
        setSelectedFile(file)

        const reader = new FileReader()
        reader.onload = () => {
            setPreviewUrl(reader.result)
        }
        reader.readAsDataURL(file)
    }

    const removeFile = () => {
        setSelectedFile(null)
        setPreviewUrl(null)
    }

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!id) return

            try {
                setPageLoading(true)
                setPackageNotFound(false)

                const [packageResponse, categoriesResponse, subcategoriesResponse] = await Promise.all([
                    axios.get(`${baseUrl}/package/${id}`, {
                        headers: { Authorization: localStorage.getItem("token") },
                    }),
                    axios.get(`${baseUrl}/category`, {
                        headers: { Authorization: localStorage.getItem("token") },
                    }),
                    axios.get(`${baseUrl}/subcategory`, {
                        headers: { Authorization: localStorage.getItem("token") },
                    }),
                ])

                const packageData = packageResponse?.data?.data
                const categoryData = categoriesResponse?.data?.data || []
                const subcategoryData = subcategoriesResponse?.data?.data || []

                setCategories(categoryData)
                setSubcategories(subcategoryData)

                if (!packageResponse?.data?.status || !packageData) {
                    setPackageNotFound(true)
                    toastify.error("Package not found")
                    return
                }

                const subcategoryId = getNormalizedId(packageData.subcategoryID)
                const categoryId = getCategoryIdBySubcategory(subcategoryId, subcategoryData)

                setFormval({
                    title: packageData.title || "",
                    description: packageData.description || "",
                    status: packageData.status || "active",
                    subcategoryID: subcategoryId,
                    categoryID: categoryId,
                })

                if (packageData.image) {
                    setExistingImage(`${baseUrl}/${packageData.image}`)
                } else {
                    setExistingImage("")
                }

                const responsePlans =
                    Array.isArray(packageData.plan) && packageData.plan.length > 0
                        ? packageData.plan
                        : [
                            {
                                validity: packageData.validity || "3",
                                regularPrice: packageData.price || "",
                                offerPrice: packageData.offerPrice || packageData.price || "",
                                is_popular: true,
                            },
                        ]

                const hasPopularPlan = responsePlans.some((plan) => Boolean(plan.is_popular))

                setPricingPlans(
                    responsePlans.map((plan, index) => {
                        const regularPrice = Number(plan.regularPrice) || Number(packageData.price) || ""
                        const offerPrice = Number(plan.offerPrice) || regularPrice || ""

                        return {
                            id: plan._id || `${plan.validity || "3"}-${index}`,
                            validity: normalizeValidityValue(plan.validity),
                            price: regularPrice ? String(regularPrice) : "",
                            offerPrice: offerPrice ? String(offerPrice) : "",
                            discountPercent: getDiscountPercent(regularPrice, offerPrice),
                            isPopular: hasPopularPlan ? Boolean(plan.is_popular) : index === 0,
                        }
                    }),
                )
            } catch (error) {
                console.error("Error fetching package data:", error)
                toastify.error(error.response?.data?.message || "Failed to fetch package data")
                setPackageNotFound(true)
            } finally {
                setPageLoading(false)
            }
        }

        fetchInitialData()
    }, [id])

    const filteredSubCat = formval.categoryID
        ? subcategories.filter((subcat) => getNormalizedId(subcat?.categoryId) === formval.categoryID)
        : subcategories

    const config = useMemo(
        () => ({
            ...editorConfig,
            height: 500,
            placeholder: "Enter package description",
        }),
        [],
    )

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!formval.title) return toastify.error("Title is required")
        if (!formval.description) return toastify.error("Description is required")
        if (!formval.categoryID) return toastify.error("Category is required")
        if (!formval.subcategoryID) return toastify.error("Subcategory is required")
        if (!formval.status) return toastify.error("Status is required")

        for (let index = 0; index < pricingPlans.length; index += 1) {
            const currentPlan = pricingPlans[index]

            if (!currentPlan.price || Number(currentPlan.price) <= 0) {
                return toastify.error(`${getValidityLabel(currentPlan.validity)} plan: Price is required`)
            }

            if (currentPlan.offerPrice && Number(currentPlan.offerPrice) > Number(currentPlan.price)) {
                return toastify.error(
                    `${getValidityLabel(currentPlan.validity)} plan: Offer price cannot be greater than regular price`,
                )
            }
        }

        setLoading(true)

        try {
            const planPayload = pricingPlans.map((plan) => ({
                validity: Number(plan.validity),
                regularPrice: Number(plan.price),
                offerPrice: plan.offerPrice ? Number(plan.offerPrice) : Number(plan.price),
                is_popular: !!plan.isPopular,
            }))

            const formData = new FormData()
            formData.append("title", formval.title)
            formData.append("description", formval.description)
            formData.append("subcategoryID", formval.subcategoryID)
            formData.append("status", formval.status)
            formData.append("plan", JSON.stringify(planPayload))

            if (selectedFile) {
                formData.append("image", selectedFile)
            }

            const response = await axios.patch(`${baseUrl}/package/${id}`, formData, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (!response?.data?.status) {
                toastify.error("An error occurred while updating package")
                return
            }

            toastify.success("Package updated successfully")
            navigate("/test-packages")
        } catch (error) {
            console.error("Error updating package:", error)
            toastify.error(error.response?.data?.message || "Failed to update package")
        } finally {
            setLoading(false)
        }
    }

    if (pageLoading) {
        return (
            <section className="main-sec">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 text-center">
                            <div className="loading-container" style={{ padding: "4rem 0" }}>
                                <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#7436A9" />
                                <h4 style={{ marginTop: "1rem", color: "#666" }}>Loading package data...</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    if (packageNotFound) {
        return (
            <section className="main-sec">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 text-center">
                            <div className="error-container" style={{ padding: "4rem 0" }}>
                                <h4 style={{ color: "#ef5350", marginBottom: "1rem" }}>Package Not Found</h4>
                                <p style={{ color: "#666", marginBottom: "2rem" }}>
                                    The package you&apos;re looking for doesn&apos;t exist or has been deleted.
                                </p>
                                <Link to="/test-packages" className="btn btn-info text-white">
                                    <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                                    Back to Packages
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    return (
        <>
            <style>{`
                .main-sec {
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    min-height: 100vh;
                    padding: 2rem 0;
                }
                .dashboard-title { margin-bottom: 1.5rem; }
                .dash-head { color: #333; font-size: 1.8rem; font-weight: 700; margin: 0; }
                .cards {
                    background: white;
                    border-radius: 15px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    margin-bottom: 2rem;
                }
                .form-section {
                    margin-bottom: 2rem;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 1.5rem;
                }
                .form-section:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .section-title {
                    color: #7436A9;
                    font-weight: 650;
                    margin-bottom: 1.5rem;
                    font-size: 1.2rem;
                }

                .pricing-plan-card {
                    background: #f8f9ff;
                    border: 2px solid #e8e8f8;
                    border-radius: 12px;
                    padding: 1.25rem;
                    margin-bottom: 1rem;
                    position: relative;
                    transition: all 0.3s ease;
                }
                .pricing-plan-card:hover {
                    border-color: #7436A9;
                    box-shadow: 0 4px 15px rgba(116, 54, 169, 0.1);
                }
                .plan-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                }
                .plan-badge {
                    background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
                    color: white;
                    padding: 0.3rem 0.8rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }
                .plan-remove-btn {
                    background: #ef5350;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                    font-size: 0.8rem;
                }
                .plan-remove-btn:hover {
                    background: #c62828;
                    transform: scale(1.1);
                }
                .add-plan-btn {
                    background: transparent;
                    color: #7436A9;
                    border: 2px dashed #7436A9;
                    border-radius: 8px;
                    padding: 0.5rem 1.2rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    width: auto;
                    font-size: 0.85rem;
                }
                .add-plan-btn:hover {
                    background: rgba(116, 54, 169, 0.08);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(116, 54, 169, 0.15);
                }
                .discount-badge {
                    display: inline-block;
                    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
                    color: white;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    margin-left: 0.5rem;
                    vertical-align: middle;
                }
                .existing-image-wrapper {
                    margin-bottom: 1rem;
                    text-align: center;
                }
                .existing-image-title {
                    color: #666;
                    font-size: 0.9rem;
                    margin-bottom: 0.75rem;
                }
                .existing-image {
                    max-width: 200px;
                    max-height: 200px;
                    border-radius: 10px;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                }
                .file-upload-container { margin-bottom: 1.5rem; }
                .file-upload-area {
                    border: 2px dashed #ddd;
                    border-radius: 10px;
                    padding: 2rem;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                }
                .file-upload-area.dragging {
                    border-color: #7436A9;
                    background: rgba(102, 126, 234, 0.1);
                }
                .file-upload-area:hover { border-color: #7436A9; }
                .file-upload-icon { font-size: 3rem; color: #7436A9; margin-bottom: 1rem; }
                .file-upload-text { font-size: 1.1rem; font-weight: 600; color: #333; margin-bottom: 0.5rem; }
                .file-upload-subtext { color: #666; font-size: 0.9rem; }
                .file-upload-input {
                    position: absolute; top: 0; left: 0;
                    width: 100%; height: 100%; opacity: 0; cursor: pointer;
                }
                .file-preview-container { position: relative; display: inline-block; }
                .file-preview {
                    max-width: 200px; max-height: 200px;
                    border-radius: 10px; box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                }
                .file-remove-btn {
                    position: absolute; top: -10px; right: -10px;
                    background: #ef5350; color: white; border: none;
                    border-radius: 50%; width: 30px; height: 30px;
                    cursor: pointer; display: flex; align-items: center; justify-content: center;
                }
                .file-info { margin-top: 1rem; font-size: 0.9rem; color: #666; }
                .btn-for-add {
                    background: linear-gradient(135deg, #7436A9 0%, #764ba2 100%);
                    border: none; border-radius: 8px;
                    padding: 0.75rem 2rem; font-weight: 600;
                    transition: all 0.3s ease; color: white;
                }
                .btn-for-add:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
                }
                .btn-for-add:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
                .btn-info {
                    background: #4facfe; border: none; border-radius: 8px;
                    padding: 0.75rem 1.5rem; font-weight: 600; transition: all 0.3s ease;
                }
                .btn-info:hover {
                    background: #2f86fe; transform: translateY(-2px);
                    box-shadow: 0 10px 20px rgba(79, 172, 254, 0.3);
                }
                .loading-spinner {
                    width: 20px; height: 20px;
                    border: 2px solid #f3f3f3; border-top: 2px solid #7436A9;
                    border-radius: 50%; animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @media (max-width: 768px) {
                    .cards { padding: 1.5rem; }
                    .form-section { padding: 1rem; }
                }
            `}</style>

            <section className="main-sec">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="dashboard-title">
                                <h4 className="dash-head">
                                    <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                                    Edit Package Testing (Admin)
                                </h4>
                            </div>
                            <div className="custom-bredcump">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                                        <li className="breadcrumb-item"><Link to="/test-packages">Packages</Link></li>
                                        <li className="breadcrumb-item active" aria-current="page">Edit Package Testing</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                        <div className="col-lg-6 d-flex justify-content-end">
                            <Link to="/test-packages" className="btn btn-info text-white">
                                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                                Back
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="cards edit-usr">
                                <form onSubmit={handleSubmit}>
                                    <div className="form-section">
                                        <h5 className="section-title">Package Details</h5>
                                        <div className="row">
                                            <div className="col-lg-12 mb-4">
                                                <label htmlFor="title" className="form-label">
                                                    Title<sup className="text-danger">*</sup>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="title"
                                                    placeholder="Enter package title"
                                                    onChange={changeHandler}
                                                    value={formval.title}
                                                />
                                            </div>
                                            <div className="col-lg-12 mb-4">
                                                <label htmlFor="description" className="form-label">
                                                    Description<sup className="text-danger">*</sup>
                                                </label>
                                                <JoditEditor
                                                    value={formval.description || ""}
                                                    config={config}
                                                    onChange={(value) => {
                                                        let content = value
                                                        const youtubeRegex =
                                                            /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})[^\s]*/g

                                                        content = content.replace(youtubeRegex, (match, videoId) => {
                                                            return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
                                                        })

                                                        setFormval((previousValue) => ({
                                                            ...previousValue,
                                                            description: content,
                                                        }))
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h5 className="section-title">Pricing Plans</h5>

                                        {pricingPlans.map((plan) => (
                                            <div className="pricing-plan-card" key={plan.id}>
                                                <div className="plan-header">
                                                    <span className="plan-badge">
                                                        {getValidityLabel(plan.validity)}
                                                        {plan.discountPercent && Number(plan.discountPercent) > 0 && (
                                                            <span className="discount-badge">{plan.discountPercent}% OFF</span>
                                                        )}
                                                        {plan.isPopular && (
                                                            <span className="badge bg-warning text-dark ms-2" style={{ fontWeight: "700" }}>
                                                                POPULAR
                                                            </span>
                                                        )}
                                                    </span>
                                                    {pricingPlans.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="plan-remove-btn"
                                                            onClick={() => removePricingPlan(plan.id)}
                                                            title="Remove this plan"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="row">
                                                    <div className="col-lg-3 mb-3">
                                                        <label className="form-label">Validity<sup className="text-danger">*</sup></label>
                                                        <select
                                                            className="form-control"
                                                            value={plan.validity}
                                                            onChange={(event) => updatePlanField(plan.id, "validity", event.target.value)}
                                                        >
                                                            {VALIDITY_OPTIONS.map((option) => (
                                                                <option
                                                                    key={option.value}
                                                                    value={option.value}
                                                                    disabled={
                                                                        usedValidities.includes(option.value) &&
                                                                        plan.validity !== option.value
                                                                    }
                                                                >
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-lg-3 mb-3">
                                                        <label className="form-label">
                                                            Regular Price (Rs)<sup className="text-danger">*</sup>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="e.g. 2999"
                                                            value={plan.price}
                                                            onChange={(event) => updatePlanField(plan.id, "price", event.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-lg-3 mb-3">
                                                        <label className="form-label">Offer Price (Rs)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="e.g. 999"
                                                            value={plan.offerPrice}
                                                            onChange={(event) => updatePlanField(plan.id, "offerPrice", event.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-lg-3 mb-3 d-flex align-items-center" style={{ paddingTop: "1.8rem" }}>
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`popular-${plan.id}`}
                                                                checked={plan.isPopular || false}
                                                                onChange={(event) => handlePopularChange(plan.id, event.target.checked)}
                                                                style={{ cursor: "pointer" }}
                                                            />
                                                            <label
                                                                className="form-check-label ms-2"
                                                                htmlFor={`popular-${plan.id}`}
                                                                style={{ cursor: "pointer", fontWeight: "650", color: "#555" }}
                                                            >
                                                                Popular Plan
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {pricingPlans.length < 3 && (
                                            <button type="button" className="add-plan-btn" onClick={addPricingPlan}>
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Add Another Plan
                                            </button>
                                        )}
                                    </div>

                                    <div className="form-section">
                                        <h5 className="section-title">Package Image</h5>

                                        {existingImage && !selectedFile && (
                                            <div className="existing-image-wrapper">
                                                <div className="existing-image-title">Current package image</div>
                                                <img
                                                    src={existingImage}
                                                    alt="Current package"
                                                    className="existing-image"
                                                    onError={(event) => {
                                                        event.target.style.display = "none"
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <div className="file-upload-container">
                                            <div
                                                className={`file-upload-area ${isDragging ? "dragging" : ""}`}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                            >
                                                {!selectedFile ? (
                                                    <>
                                                        <div className="file-upload-icon">
                                                            <FontAwesomeIcon icon={faUpload} />
                                                        </div>
                                                        <div className="file-upload-text">
                                                            {existingImage ? "Upload a new image to replace current one" : "Drag & Drop your image here"}
                                                        </div>
                                                        <div className="file-upload-subtext">or click to browse files</div>
                                                        <input
                                                            type="file"
                                                            className="file-upload-input"
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                    </>
                                                ) : (
                                                    <div className="file-preview-container">
                                                        <img src={previewUrl || "/placeholder.svg"} alt="Preview" className="file-preview" />
                                                        <button type="button" className="file-remove-btn" onClick={removeFile}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                        <div className="file-info">
                                                            {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h5 className="section-title">Category Selection</h5>
                                        <div className="row">
                                            <div className="col-lg-6 mb-4">
                                                <label htmlFor="categoryID" className="form-label">
                                                    Category<sup className="text-danger">*</sup>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    name="categoryID"
                                                    onChange={changeHandler}
                                                    value={formval.categoryID}
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map((category) => (
                                                        <option key={category._id} value={category._id}>
                                                            {category?.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-lg-6 mb-4">
                                                <label htmlFor="subcategoryID" className="form-label">
                                                    Subcategory<sup className="text-danger">*</sup>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    name="subcategoryID"
                                                    onChange={changeHandler}
                                                    value={formval.subcategoryID}
                                                >
                                                    <option value="">Select Subcategory</option>
                                                    {filteredSubCat.map((subcategory) => (
                                                        <option key={subcategory._id} value={subcategory._id}>
                                                            {subcategory.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h5 className="section-title">Status</h5>
                                        <div className="row">
                                            <div className="col-lg-6 mb-4">
                                                <label htmlFor="status" className="form-label">
                                                    Status<sup className="text-danger">*</sup>
                                                </label>
                                                <select className="form-control" name="status" onChange={changeHandler} value={formval.status}>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-lg-12 text-center mt-4">
                                        <button type="submit" className="btn btn-for-add text-white" disabled={loading}>
                                            {loading && <div className="loading-spinner"></div>}
                                            <FontAwesomeIcon icon={faSave} className="me-2" />
                                            {loading ? "Updating..." : "Update Package"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
